import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { IconButton } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import styles from './styles';
import ActionModalTitleDescription from './ActionModalTitleDescription';
import ActionModalCta from './ActionModalCta';
import { MODAL_ID } from '../shared/Constants';
import ActionModalImage from './ActionModalImage';
import { showModalWindow, dataRetrieval } from '../../../util/gtm';

/**
 * @name EirActionModal - jsx component for reusable action modal
 * @param {boolean} open - specifies when modal is open or close.
 * @param {Function} handleClose - function to be executed when modal needs to be closed,(usually toggles value of open variable
 * @param {string} [imageSrc] - URL of image to be displayed in modal.
 * @param {string} [iconAlt] - alternate text to be displayed in case image fails to load.
 * @param {string} [desktopImageAlt] - alternate text to be displayed in case image fails to load.
 * @param {string} [mobileImageAlt] - alternate text to be displayed in case image fails to load.
 * @param {string} [title] - title of modal.
 * @param {string} [description] - description/subtitle of the modal.
 * @param {array} [cta] - array of object's which contain all cta details
 * @param {string} [cta.text] - text of cta.
 * @param {Function} [cta.action] - function to handle onclick event of cta.
 * @param {string} [cta.type] - type of cta (solid ,contained etc)
 * @param {array} [altcta] - array of object's which contain all altcta details
 * @param {string} [altcta.text] - text of altcta.
 * @param {Function} [altcta.action] - function to handle onclick event of alternate cta.
 * @param {boolean} [disableCloseOnBackdropClick] - if true then modal will not close on clicking the modal backdrop.
 * @param {boolean} [disableCloseOnEscapeKeyDown] - if true then modal will not close on pressing escape key.
 * @param {boolean} [hideImageIcon] - if true then image and icon will not be shown.
 */
const EirActionModal = ({
  id,
  open,
  handleClose,
  desktopImageSrc,
  desktopImageAlt,
  mobileImageAlt,
  mobileImageSrc,
  title,
  description,
  cta,
  altcta,
  iconSrc,
  iconAlt,
  disableCloseOnBackdropClick,
  disableCloseOnEscapeKeyDown,
  disablecta,
  desktopCurveImg,
  mobileCurverImg,
  desktopCurveImgAlt,
  mobileCurverImgAlt,
  enableBackCTA = false,
  backCTAFunc,
  enableCloseCTA,
  closeHandler,
  onLoadingDisablecta,
  load,
  hideImageIcon,
}) => {
  const theme = useTheme();
  const router = useRouter();

  const containerRef = useRef(null);
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  // We wait until the actual Strapi data arrives before we force focus.
  const isReady = Boolean(title && description);
  // We manually shift focus ONLY when the modal is open AND the data is fully loaded.

  // This keeps the screen reader silent during the visual loading phase.
  useEffect(() => {
    if (open && isReady) {
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open, isReady]);

  const {
    NBI_ORDER_PENDING,
    NBI_ORDER_RESERVED,
    NBI_ACTIVE_LINES,
    NBI_INTERVENTION_AREA,
    NBI_MULTIPLE_PREMISES,
    NBI_SURVEY_REQUIRED,
  } = MODAL_ID;

  const listOfValues = [
    'ERROR_2-contact_us',
    'data_retrieval_issue',
    'prequal_not_responding',
    NBI_ORDER_PENDING,
    NBI_ORDER_RESERVED,
    NBI_ACTIVE_LINES,
    NBI_INTERVENTION_AREA,
    NBI_MULTIPLE_PREMISES,
    NBI_SURVEY_REQUIRED,
  ];

  useEffect(() => {
    if (open && id) showModalWindow(id, router.asPath);
    if (open && listOfValues.includes(id)) dataRetrieval(id, router.asPath);
  }, [open, id]);

  const handleDefaultClose = (event, reason) => {
    if (
      !(
        (reason === 'backdropClick' && disableCloseOnBackdropClick === true) ||
        (reason === 'escapeKeyDown' && disableCloseOnEscapeKeyDown === true)
      )
    ) {
      handleClose();
    }
  };

  return (
    <Modal
      id={id}
      open={open} // Modal opens visually immediately
      keepMounted={false}
      disableAutoFocus // CRITICAL: Prevents MUI from auto-announcing before data arrives
      // disableRestoreFocus is removed or set to false to allow focus to return to the trigger button when closed
      onClose={(event, reason) => {
        handleDefaultClose(event, reason);
      }}
    >
      <Box
        sx={styles(theme).modalRoot}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={containerRef}
        aria-busy={!isReady}
      >
        <Box sx={styles(theme).modalContainer}>
          <Box sx={{ ...styles(theme).imageAndText, height: hideImageIcon ? '1rem' : '10.3rem' }}>
            {enableBackCTA && (
              <IconButton
                sx={styles(theme).backCTA}
                onClick={() => {
                  backCTAFunc(false);
                }}
              >
                <KeyboardBackspaceIcon sx={{ color: `${theme.palette.secondary.errorCTA}` }} />
              </IconButton>
            )}
            {enableCloseCTA === false && enableCloseCTA !== undefined && (
              <IconButton
                sx={styles(theme).closeCTA}
                onClick={() => {
                  closeHandler();
                }}
                disabled={onLoadingDisablecta}
              >
                <CloseIcon sx={{ color: `${theme.palette.common.white}` }} />
              </IconButton>
            )}

            {(desktopImageSrc?.search('local/') >= 0 ||
              mobileImageSrc?.search('local/') >= 0 ||
              iconSrc?.search('local/') >= 0 ||
              desktopImageSrc?.search('undefined') < 0 ||
              mobileImageSrc?.search('undefined') < 0 ||
              iconSrc?.search('undefined') < 0) && (
              <ActionModalImage
                desktopImageSrc={desktopImageSrc}
                desktopImageAlt={desktopImageAlt}
                mobileImageSrc={mobileImageSrc}
                mobileImageAlt={mobileImageAlt}
                iconSrc={iconSrc}
                iconAlt={iconAlt}
                desktopCurveImg={desktopCurveImg}
                mobileCurverImg={mobileCurverImg}
                desktopCurveImgAlt={desktopCurveImgAlt}
                mobileCurverImgAlt={mobileCurverImgAlt}
                load={load}
              />
            )}
          </Box>
          <Box>
            <Box sx={{ ...styles(theme).descriptionBox, mt: mobileImageSrc && '10px' }}>
              <ActionModalTitleDescription
                titleId={titleId}
                descriptionId={descriptionId}
                title={title}
                description={description}
                load={load}
              />
            </Box>
            <ActionModalCta cta={cta} altcta={altcta} disablecta={disablecta} />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EirActionModal;
